import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
  index
}: FAQItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.3 + index * 0.1
  }} className="border border-[#E8DDD0] rounded-xl overflow-hidden bg-white" data-id="element-149">
      <button onClick={onToggle} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-[#5B8A7A] focus:ring-inset" aria-expanded={isOpen} data-id="element-150">
        <span className="font-['Playfair_Display'] font-semibold text-[#2D4A3E] text-lg pr-4" data-id="element-151">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-[#5B8A7A] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-152" />
      </button>
      <AnimatePresence data-id="element-153">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-154">
            <div className="px-6 pb-5" data-id="element-155">
              <p className="font-['Source_Sans_3'] text-[#3A3A3A] leading-relaxed" data-id="element-156">
                {answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function ProofAndFAQ() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const milestones = [{
    day: 'Day 3',
    result: 'The screaming at the sight of new food stops.'
  }, {
    day: 'Day 5',
    result: 'Your child will interact with (touch or smell) a food they previously refused to look at.'
  }, {
    day: 'Day 7',
    result: 'You will sit through a full meal without making a backup plate or engaging in a negotiation.'
  }];
  const faqs = [{
    question: 'Will my child go to bed hungry?',
    answer: 'No. Every meal includes an "Anchor Food" they already love. They choose how much to eat; you choose what is served.'
  }, {
    question: 'Does this work for older kids?',
    answer: 'It is most effective for ages 2-8, but the sensory logic of the Exposure Ladder works for all ages, including adults.'
  }, {
    question: 'How long does dinner take?',
    answer: 'This protocol actually shortens dinner because you stop the 20-minute struggle of begging them to eat.'
  }];
  return <section ref={ref} className="w-full bg-[#FFF8F0] py-10 md:py-16 px-4" data-id="element-157">
      <div className="max-w-3xl mx-auto" data-id="element-158">
        {/* Proof Timeline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-159">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl font-bold text-[#2D4A3E] mb-6 text-center" data-id="element-160">
            What Changes in Just 7 Days
          </h2>

          <div className="relative" data-id="element-161">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#E8DDD0] hidden md:block" data-id="element-162" />

            <div className="space-y-6" data-id="element-163">
              {milestones.map((milestone, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -30
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.2 + index * 0.15
            }} className="flex items-start gap-6" data-id="element-164">
                  <div className="relative z-10" data-id="element-165">
                    <div className="w-12 h-12 rounded-full bg-[#5B8A7A] flex items-center justify-center shadow-md" data-id="element-166">
                      <span className="font-['Source_Sans_3'] font-bold text-white text-sm" data-id="element-167">
                        {milestone.day.split(' ')[1]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 bg-white p-5 rounded-xl shadow-sm border border-[#E8DDD0]" data-id="element-168">
                    <span className="font-['Playfair_Display'] font-semibold text-[#5B8A7A] text-sm uppercase tracking-wide" data-id="element-169">
                      {milestone.day}
                    </span>
                    <p className="font-['Source_Sans_3'] text-[#3A3A3A] text-lg mt-1 leading-relaxed" data-id="element-170">
                      {milestone.result}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.3
      }} data-id="element-171">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-4xl font-bold text-[#2D4A3E] mb-6 text-center" data-id="element-172">
            Common Questions
          </h2>
          <div className="space-y-4" data-id="element-173">
            {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openFAQ === index} onToggle={() => setOpenFAQ(openFAQ === index ? null : index)} index={index} data-id="element-174" />)}
          </div>
        </motion.div>
      </div>
    </section>;
}